/**
 * TrueLineWellness — product.js
 *   <variant-picker>           selects the variant from option inputs, updates the
 *                              form, URL, gallery and re-renders price/inventory/SKU
 *                              through the Section Rendering API (exact Markets pricing).
 *   <product-gallery>          swipeable media with thumbnails.
 *   <product-recommendations>  loads Shopify product recommendations.
 */
(function () {
  'use strict';

  var TLW = (window.TLW = window.TLW || {});
  var theme = window.theme || {};
  var strings = theme.strings || {};
  var ui = TLW.ui;

  /* ---------------------------------------------------------------------- */
  /* <variant-picker>                                                        */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', class extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-variants]');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.sectionId = this.dataset.section;
        this.form = document.getElementById(this.dataset.form);
        this.addEventListener('change', () => this.onChange());
        this.markAvailability();
      }

      selectedOptions() {
        const inputs = Array.from(this.querySelectorAll('[data-option-index]'));
        const values = [];
        inputs.forEach((input) => {
          const i = Number(input.dataset.optionIndex);
          if (input.tagName === 'SELECT') values[i] = input.value;
          else if (input.checked) values[i] = input.value;
        });
        return values;
      }

      findVariant(options) {
        return this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      }

      onChange() {
        const options = this.selectedOptions();
        const variant = this.findVariant(options);

        this.querySelectorAll('fieldset').forEach((fs) => {
          const checked = fs.querySelector('input:checked');
          const label = fs.querySelector('[data-option-label]');
          if (checked && label) label.textContent = checked.value;
        });
        this.markAvailability();
        this.updateForm(variant);

        if (!variant) return;
        if (this.dataset.updateUrl === 'true' && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
        const section = this.closest('[data-product-section]');
        if (section) section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant }, bubbles: false }));
        if (this.dataset.render === 'client') this.renderClient(variant);
        else this.renderProductInfo(variant);
      }

      /**
       * Featured-product sections live on other templates, so the Section
       * Rendering API can't re-render them for a variant; update from the
       * variant JSON instead (prices are already in the customer's currency).
       */
      renderClient(variant) {
        const price = document.getElementById('Price-' + this.sectionId);
        if (price) {
          const current = price.querySelector('.price__current');
          const compare = price.querySelector('.price__compare');
          const save = price.querySelector('.price__save');
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          if (current) current.lastChild.textContent = ui.formatMoney(variant.price);
          if (compare) {
            compare.hidden = !onSale;
            if (onSale) compare.lastChild.textContent = ui.formatMoney(variant.compare_at_price);
          }
          if (save) {
            save.hidden = !onSale;
            if (onSale) save.textContent = (strings.savePercent || 'Save [percent]%').replace('[percent]', Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price));
          }
        }
        const sku = document.getElementById('Sku-' + this.sectionId);
        if (sku) sku.textContent = variant.sku ? 'SKU: ' + variant.sku : '';
      }

      /** Mark option values that have no available variant with the other current selections. */
      markAvailability() {
        const selected = this.selectedOptions();
        this.querySelectorAll('[data-option-index]').forEach((input) => {
          const i = Number(input.dataset.optionIndex);
          const candidates = input.tagName === 'SELECT' ? Array.from(input.options) : [input];
          candidates.forEach((el) => {
            const test = selected.slice();
            test[i] = el.value;
            const match = this.variants.find((v) => v.options.every((val, j) => val === test[j]));
            const available = !!(match && match.available);
            const holder = el.closest('.variant-pill') || el;
            holder.classList.toggle('is-unavailable', !available);
          });
        });
      }

      updateForm(variant) {
        if (!this.form) return;
        const idInput = this.form.querySelector('[data-variant-id]');
        const button = this.form.querySelector('[data-add-button]');
        const label = this.form.querySelector('[data-add-label]');
        if (idInput) {
          idInput.value = variant ? variant.id : '';
          idInput.disabled = !variant;
        }
        if (!button || !label) return;
        if (!variant) {
          button.disabled = true;
          label.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          button.disabled = true;
          label.textContent = strings.soldOut || 'Sold out';
        } else {
          button.disabled = false;
          label.innerHTML = ui.escapeHtml(strings.addToCart || 'Add to cart') +
            '<span class="product__atc-price"> &mdash; <span data-atc-price>' + ui.escapeHtml(ui.formatMoney(variant.price)) + '</span></span>';
        }
      }

      /** Re-render price, inventory and SKU from Shopify for the chosen variant. */
      renderProductInfo(variant) {
        const url = this.dataset.url + (this.dataset.url.indexOf('?') > -1 ? '&' : '?') + 'variant=' + variant.id + '&section_id=' + this.sectionId;
        if (this.abort) this.abort.abort();
        this.abort = 'AbortController' in window ? new AbortController() : null;
        fetch(url, { signal: this.abort ? this.abort.signal : undefined })
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            ['Price-', 'Inventory-', 'Sku-'].forEach((prefix) => {
              const id = prefix + this.sectionId;
              const fresh = doc.getElementById(id);
              const current = document.getElementById(id);
              if (fresh && current) current.innerHTML = fresh.innerHTML;
            });
          })
          .catch(() => {});
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* <product-gallery>                                                       */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', class extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.slides = Array.from(this.querySelectorAll('.product__slide'));
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        if (!this.viewport) return;

        this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(t.dataset.galleryThumb)));
        this.viewport.addEventListener('scroll', () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.syncFromScroll());
        }, { passive: true });

        const section = this.closest('[data-product-section]');
        if (section) {
          section.addEventListener('variant:change', (e) => {
            const media = e.detail.variant && e.detail.variant.featured_media;
            if (media) this.goTo(String(media.id));
          });
        }
        // Start on the selected variant's image
        const active = this.slides.find((s) => s.classList.contains('is-active'));
        if (active && active !== this.slides[0]) this.goTo(active.dataset.mediaId, true);
      }

      goTo(mediaId, instant) {
        const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
        if (!slide) return;
        this.viewport.scrollTo({
          left: slide.offsetLeft,
          behavior: instant || ui.reducedMotion() ? 'auto' : 'smooth'
        });
        this.setActive(slide);
      }

      syncFromScroll() {
        const x = this.viewport.scrollLeft;
        const w = this.viewport.clientWidth || 1;
        const slide = this.slides[Math.round(x / w)];
        if (slide) this.setActive(slide);
      }

      setActive(slide) {
        this.slides.forEach((s) => {
          const active = s === slide;
          s.classList.toggle('is-active', active);
          // pause videos that scroll out of view
          if (!active) s.querySelectorAll('video').forEach((v) => v.pause());
        });
        this.thumbs.forEach((t) => {
          const active = t.dataset.galleryThumb === slide.dataset.mediaId;
          t.classList.toggle('is-active', active);
          if (active) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* <product-recommendations>                                               */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.loaded || !this.dataset.url) return;
        const load = () => {
          this.loaded = true;
          fetch(this.dataset.url)
            .then((r) => r.text())
            .then((html) => {
              const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim()) {
                this.innerHTML = fresh.innerHTML;
                if (TLW.motion) TLW.motion.initReveal(this);
              }
            })
            .catch(() => {});
        };
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting) return;
            io.disconnect();
            load();
          }, { rootMargin: '0px 0px 400px 0px' });
          io.observe(this);
        } else {
          load();
        }
      }
    });
  }
})();
